import { NotificationBell as BellView } from "@loot-os/design-system/domain/notifications/NotificationBell";
import { formatUnreadBadge } from "@loot-os/design-system/domain/notifications/notificationFormat";
import { useEffect, useState } from "react";

import { lazyOverlay, useLazyOverlay, usePreloadWhenIdle } from "../../lib/lazyOverlay.js";
import {
  bellRangThisSession,
  markBellRang,
  notificationsEnabled,
  useUnreadNotifications,
} from "../../lib/notifications.js";
import { useShopper } from "../../lib/shopper.js";
import { useTenant } from "../../lib/tenant.js";

/** The panel, its own chunk: fetched once the page is idle, drawn when the bell opens. */
const LazyPanel = lazyOverlay(() =>
  import("./NotificationBellPanel.js").then((module) => module.NotificationBellPanel),
);

/** The header's bell, for a signed-in shopper on a store with notifications on. */
export function NotificationBell() {
  const { user } = useShopper();
  const { config } = useTenant();
  if (!user || !notificationsEnabled(config)) return null;
  return <Bell userId={user.id} />;
}

/**
 * The design system's bell and its unread badge, opening the panel in a popover, or a
 * full-screen sheet on a phone. Mounted once, it also keeps the count live and toasts what
 * arrives.
 */
function Bell({ userId }: { userId: string }) {
  const unreadCount = useUnreadNotifications();
  const [open, setOpen] = useState(false);
  usePreloadWhenIdle(LazyPanel);
  const Panel = useLazyOverlay(LazyPanel, open, () => setOpen(false));
  // It rings for unread notifications it finds on load once a browser session, then only when one
  // arrives, so a shopper who leaves them unread isn't rung at on every visit.
  const [ringOnLoad] = useState(() => !bellRangThisSession(userId));
  const hasUnread = unreadCount !== undefined && unreadCount > 0;
  useEffect(() => {
    if (hasUnread) markBellRang(userId);
  }, [hasUnread, userId]);
  const badge = formatUnreadBadge(unreadCount ?? 0);
  return (
    <BellView
      // The name says what the badge shows ("9+" past nine), so the two never disagree (WCAG 2.5.3).
      label={badge ? `Notifications, ${badge} unread` : "Notifications"}
      badge={badge}
      unreadCount={unreadCount}
      ringOnLoad={ringOnLoad}
      panelLabel="Notifications"
      open={open}
      onOpenChange={setOpen}
    >
      {({ phone }) =>
        Panel && (
          <Panel unreadCount={unreadCount ?? 0} onClose={() => setOpen(false)} showClose={phone} />
        )
      }
    </BellView>
  );
}
